import { useEffect, useState } from 'react';
import { Calendar, Rocket, TrendingUp } from 'lucide-react';

const milestones = [
  {
    year: '2023',
    title: 'First Prototype Built',
    description: 'Created for The Summit California to solve spreadsheet chaos',
    icon: Rocket,
  },
  {
    year: '2024',
    title: 'Sysco Integration Launched',
    description: 'Full Supabase backend and seamless Sysco ordering integration',
    icon: Calendar,
  },
  {
    year: '2025',
    title: 'Nationwide Expansion',
    description: 'Now serving camps and retreats across the country',
    icon: TrendingUp,
  },
];

export const OurStory = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    const element = document.getElementById('our-story');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <section id="our-story" className="py-20 bg-sand">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-slate mb-6">
            Born from Real Camp Kitchens
          </h2>
          <p className="font-inter text-lg text-slate/80 max-w-3xl mx-auto leading-relaxed">
            CampChef Pro started as a simple solution to help one camp in California manage chaotic food orders and menu planning. 
            After years of frustration with spreadsheets and manual Sysco uploads, our founders — camp directors and kitchen managers — 
            teamed up with developers to build an all-in-one tool. Today, CampChef Pro powers meal operations for camps and retreats 
            across the country.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-deep-teal via-copper to-deep-teal transform -translate-y-1/2" />

          {/* Milestone Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
            {milestones.map((milestone, index) => {
              const Icon = milestone.icon;
              return (
                <div
                  key={milestone.year}
                  className={`bg-white rounded-lg p-8 shadow-lg border-2 border-deep-teal/20 transition-all duration-500 hover:shadow-xl hover:scale-105 ${
                    isVisible
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-deep-teal to-copper">
                    <Icon className="w-8 h-8 text-ivory" />
                  </div>
                  <div className="text-center">
                    <div className="font-playfair text-3xl font-bold text-copper mb-2">
                      {milestone.year}
                    </div>
                    <h3 className="font-playfair text-xl font-semibold text-slate mb-3">
                      {milestone.title}
                    </h3>
                    <p className="font-inter text-slate/70">
                      {milestone.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
